import React, { useEffect, forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  DiscardModal,
  EIconName,
  InputReadOnly,
  UrlInput
} from '@ppmcore/seven-ppm-core-shared-components-react';
import {
  TUrlInputRef
} from '@ppmcore/seven-ppm-core-shared-components-react/dist/cjs/types/lib/components/url-input/url-input';

// Store entities
import { updateCompanyWebUrl } from '../../../../../../store/company/company.thunks';
import { openNotification } from '../../../../../../store/app-notifications/app-notifications.thunks';

// Styles
import './web-app-url.scss';

// Models
import { ICompanyProfile } from '../../../../../../interfaces/company.interfaces';

export type TWebAppUrlRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TWebAppUrlProps = {
  companyLinkNotEditable: string;
  companyLinkEditable: string;
  company: ICompanyProfile;
};

export const WebAppUrl = forwardRef<TWebAppUrlRef, TWebAppUrlProps>((
  { companyLinkEditable, companyLinkNotEditable, company }: TWebAppUrlProps, ref
) => {
  const [form] = Form.useForm<{ url_code: string }>();
  const inputUrlRef = useRef<TUrlInputRef>(null);

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    form.setFieldsValue({
      url_code: company?.url_code ?? '',
    });
    toggleEditMode();
  }

  const onCopyHandler = (): void => {
    dispatch(openNotification({
      type: 'success',
      description: 'Successfully copied!'
    }));
  }

  const onCopyErrorHandler = (): void => {
    dispatch(openNotification({
      type: 'error',
      description: 'Copy failed! Try again'
    }));
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      form.setFieldsValue({
        url_code: company?.url_code ?? '',
      });
      return;
    }
    toggleDiscardModal();
  }

  const onValuesChange = (_: { [key: string]: string | string[] }, { url_code }: {
    url_code: string,
  }): void => {
    setHasChanges(companyLinkEditable !== url_code && !!inputUrlRef.current?.isValid());
    inputUrlRef.current?.changeErrorMessage('');
    inputUrlRef.current?.changeValidStatus(!!inputUrlRef.current?.isValid(url_code));
  }

  const saveChanges = async (): Promise<void> => {
    const { url_code } = form.getFieldsValue();
    const trimUrl = url_code.trim();

    setSavingChanges(true);
    const { payload } = await dispatch(updateCompanyWebUrl(trimUrl));
    setSavingChanges(false);

    if (!payload) {
      inputUrlRef.current?.changeErrorMessage('That URL is not available. Please, try a different URL.');
      inputUrlRef.current?.changeValidStatus(false);
      setHasChanges(false);
      return;
    }
    setEditMode(false);
    setHasChanges(false);
  }

  useImperativeHandle(ref, () => ({
    isHasChanges(): boolean {
      return hasChanges;
    },
    resetState(): void {
      form.setFieldsValue({
        url_code: company?.url_code ?? '',
      });
      setHasChanges(false);
      setEditMode(false);
      setSavingChanges(false);
    }
  }));

  useEffect(() => {
    form.setFieldsValue({ url_code: companyLinkEditable });
  }, [companyLinkNotEditable, companyLinkEditable]);

  return (
    <div className="web-app-url">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="web-app-url--label business--label">
        <div className="label-title">
          Web App URL <CustomTooltip title={ 'This is the URL of the Paypertok Web App for your business. Use it to provide consultations to your clients.\n' +
          'To share, click on copy and paste it into emails, social media, and more.\n' +
          'Note: The page is private and not searchable.' }><CustomIcon size={'size-md'} name={ EIconName.Info }/></CustomTooltip>
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ company?.url_code ? 'Edit' : 'Add' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form className="web-app-url--body business--body-items" form={ form } autoComplete={ 'off' }
            onValuesChange={ onValuesChange }>
        <div className="body-item">
          { (!editMode && company?.url_code) &&
            <InputReadOnly label={ 'Web App URL' }
                           value={ companyLinkEditable ? `${ companyLinkNotEditable }${ companyLinkEditable }` : '' }
                           isCopyIcon={ true }
                           textEllipsis={ true }
                           icon={ <CustomIcon name={ EIconName.ContentCopy }/> } onCopy={ onCopyHandler }
                           onCopyError={ onCopyErrorHandler }/> }

          { editMode &&
            <Form.Item name={ 'url_code' }>
              <UrlInput ref={ inputUrlRef } urlPrefix={ companyLinkNotEditable } placeholder={ 'Web App URL' }/>
            </Form.Item> }
        </div>
      </Form>
    </div>
  );
})
